import { logout } from '@/utils/auth';
import { api } from '@/utils/configuration';
import { getValidAccessToken } from '@/utils/refreshToken';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import Axios from 'axios';

const excludedRoutesFromAuth: string[] = ['/registration'];

const canContinueWithoutAuth = () => {
  return excludedRoutesFromAuth.includes(window.location.pathname);
};

const PUBLIC_ENDPOINTS = [
  `${api.AUTH_URL}/refresh`,
  `${api.AUTH_URL}/register-org`,
  `${api.AUTH_URL}/login`,
  '/enumerations/state',
];

const apiClient = Axios.create({
  baseURL: '/rest',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: {
    indexes: null,
  },
});

apiClient.interceptors.request.use((config) => {
  // No extra configuration for absolute URLs (i.e.: a file in S3)
  if (config.url!.charAt(0) !== '/') {
    return config;
  }

  if (PUBLIC_ENDPOINTS.includes(config.url || '')) {
    return config;
  }

  return getValidAccessToken(canContinueWithoutAuth())
    .then((accessToken) => {
      // eslint-disable-next-line no-param-reassign
      config.headers = config.headers ?? {};
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    })
    .catch((error: string) => {
      return Promise.reject(new Axios.Cancel(error));
    });
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !canContinueWithoutAuth()) {
      logout();
    }

    throw error;
  },
);

export const crudService = {
  get: <T>(url: string, params?: AxiosRequestConfig) =>
    apiClient.get<T, AxiosResponse<T>>(url, params).then((response) => response.data),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: <T>(obj: any, url: string, params?: AxiosRequestConfig) =>
    apiClient.post<T, AxiosResponse<T>>(url, obj, params).then((response) => response.data),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: <T>(obj: any, url: string) =>
    apiClient.put<T, AxiosResponse<T>>(url, obj).then((response) => response.data),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch: <T>(obj: any, url: string) =>
    apiClient.patch<T, AxiosResponse<T>>(url, obj).then((response) => response.data),

  delete: <T>(url: string) =>
    apiClient.delete<T, AxiosResponse<T>>(url).then((response) => response.data),
};
